import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import CarCard from "@/components/CarCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Inventory() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState("all");
  const [sort, setSort] = useState("price-asc");

  useEffect(() => {
    api.get("/cars").then((r) => setCars(r.data)).finally(() => setLoading(false));
  }, []);

  const brands = useMemo(
    () => ["all", ...Array.from(new Set(cars.map((c) => c.brand).filter(Boolean))).sort()],
    [cars]
  );

  const filtered = useMemo(() => {
    let list = brand === "all" ? cars : cars.filter((c) => c.brand === brand);
    list = [...list].sort((a, b) => {
      if (sort === "price-asc") return (a.price || 0) - (b.price || 0);
      if (sort === "price-desc") return (b.price || 0) - (a.price || 0);
      if (sort === "year-desc") return (b.year || 0) - (a.year || 0);
      if (sort === "km-asc") return (a.mileage || 0) - (b.mileage || 0);
      return 0;
    });
    return list;
  }, [cars, brand, sort]);

  return (
    <div className="pt-32 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff5a00] mb-3">Valikoima</p>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-2">Autot</h1>
        <p className="text-white/50 font-mono text-sm mb-10">{filtered.length} autoa myynnissä · valikoima päivittyy jatkuvasti</p>

        <div className="flex flex-col sm:flex-row gap-4 mb-12">
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger data-testid="filter-brand" className="w-full sm:w-56 bg-[#121212] border-white/20 rounded-none text-white font-mono text-xs uppercase tracking-widest">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/20 text-white">
              {brands.map((b) => (
                <SelectItem key={b} value={b} className="font-mono text-xs uppercase">
                  {b === "all" ? "Kaikki merkit" : b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="filter-sort" className="w-full sm:w-56 bg-[#121212] border-white/20 rounded-none text-white font-mono text-xs uppercase tracking-widest">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/20 text-white">
              <SelectItem value="price-asc" className="font-mono text-xs uppercase">Halvin ensin</SelectItem>
              <SelectItem value="price-desc" className="font-mono text-xs uppercase">Kallein ensin</SelectItem>
              <SelectItem value="year-desc" className="font-mono text-xs uppercase">Uusin vuosimalli</SelectItem>
              <SelectItem value="km-asc" className="font-mono text-xs uppercase">Vähiten ajetut</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border border-white/10 bg-[#121212] animate-pulse">
                <div className="aspect-[4/3] bg-white/5" />
                <div className="p-6 space-y-3"><div className="h-5 bg-white/10 w-2/3" /><div className="h-3 bg-white/5 w-1/2" /></div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-white/50 font-mono">Ei autoja tällä valinnalla.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="inventory-grid">
            {filtered.map((car, i) => <CarCard key={car.nettiauto_id} car={car} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
